import React, { useEffect, useState } from 'react'
import { Controller, useForm } from 'react-hook-form'
import { Col, Container, Form, Row } from 'react-bootstrap'

import { useGetMeQuery, useUpdateUserIntegrationMutation } from '../../../../store/user/user'
import { selectAccountId } from '../../../../store/auth/authSlice'
import { useAppSelector } from '../../../../store/hooks'

import { MissingScopeAction } from '../../../../components/ScopedBlock/models'
import { ScopedBlock } from '../../../../components/ScopedBlock/ScopedBlock'

import { removeEmptyItems } from '../../../../common/util'

import { getDefaultIntegrationSettingsFormValues, IntegrationSettingsFormValues, merge } from '../../utils'
import { Button } from '../../../../components/Button/Button'
import { FormFeedback } from '../../../../components/Form/FormFeedback'
import { SelectDropdown, SelectOption } from '../../../../components/SelectDropdown/SelectDropdown'
import DatePicker from 'react-datepicker'
import 'react-datepicker/dist/react-datepicker.css'

export interface TaskDueDateOffset {
  value: number
  unit: 'days' | 'weeks' | 'months'
}

export interface TaskDueAllDay {
  allDay: boolean
  date: Date | null
}

const unitOptions: SelectOption[] = [
  {
    id: 'days',
    label: 'Days',
  },
  {
    id: 'weeks',
    label: 'Weeks',
  },
  {
    id: 'months',
    label: 'Months',
  },
]

const MAX_OFFSET_VALUE = 100

interface CRMIntegrationTaskSettingsProps {
  serviceId: string
}

export const CRMIntegrationTaskSettings: React.FC<CRMIntegrationTaskSettingsProps> = ({ serviceId }) => {
  const accountId = useAppSelector(selectAccountId)
  const { data: userData, isFetching: isGetMeQueryFetching } = useGetMeQuery({ accountId: accountId })
  const [updateUserIntegration, { isLoading: isUpdateUserIntegrationLoading }] = useUpdateUserIntegrationMutation()
  const [isDatePickerValid, setIsDatePickerValid] = useState(true)

  const form = useForm<IntegrationSettingsFormValues>({ defaultValues: getDefaultIntegrationSettingsFormValues(serviceId) })

  useEffect(() => {
    if (isGetMeQueryFetching || !userData) {
      return
    }
    const integration = userData.integrations?.find((i) => i.serviceId === serviceId)
    if (!integration) {
      return
    }
    if (!integration.serviceConfig?.taskDueDateOffset) {
      return
    }
    if (integration.serviceConfig?.taskDueAllDay) {
      setIsDatePickerValid(true)
      if (!integration.serviceConfig.taskDueAllDay.allDay) {
        const today = new Date()
        const zoneOffset = integration.serviceConfig.taskDueAllDay.zoneOffset / 60
        today.setHours(integration.serviceConfig.taskDueAllDay.hour - zoneOffset, integration.serviceConfig.taskDueAllDay.minute)

        form.setValue('taskDueAllDay', {
          date: today,
          allDay: integration.serviceConfig.taskDueAllDay.allDay,
        })
      } else {
        form.setValue('taskDueAllDay', {
          allDay: integration.serviceConfig.taskDueAllDay.allDay,
          date: null,
        })
      }
    }

    form.setValue('taskDueDateOffset', integration.serviceConfig.taskDueDateOffset)
  }, [isGetMeQueryFetching, userData, form, serviceId])
  const onSubmit = async (data: IntegrationSettingsFormValues) => {
    try {
      const integration = userData?.integrations?.find((i) => i.serviceId === serviceId)
      if (!integration) {
        return
      }

      const serviceConfigTaskDueAllDay =
        'taskDueAllDay' in data
          ? {
              allDay: data.taskDueAllDay?.allDay,
              hour: data.taskDueAllDay?.date?.getUTCHours(),
              minute: data.taskDueAllDay?.date?.getUTCMinutes(),
              zoneOffset: data.taskDueAllDay?.date?.getTimezoneOffset(),
            }
          : null

      const serviceConfig = merge(integration.serviceConfig || {}, {
        taskDueDateOffset: data.taskDueDateOffset,
        ...(serviceConfigTaskDueAllDay && { taskDueAllDay: serviceConfigTaskDueAllDay }),
      })

      await updateUserIntegration({
        accountId,
        integrationName: integration.serviceId,
        integrationId: integration.id,
        payload: { serviceConfig: removeEmptyItems(serviceConfig) },
      })
    } catch (e) {
      console.error(e)
    }
  }

  const handleReset = () => {
    form.reset()
  }

  if (!isUpdateUserIntegrationLoading && isGetMeQueryFetching) {
    return (
      <Container>
        <div className='skeleton mb-2' style={{ width: '100%', height: '70px' }} />
        <div className='skeleton mb-3' style={{ width: '100%', height: '70px' }} />
      </Container>
    )
  }

  return (
    <Container className='d-flex flex-column gap-3'>
      <Row>
        <h6>Default task due date</h6>
      </Row>
      <div>Set a default due date for tasks that Zocks creates after your meetings.</div>
      <Row>
        <Col className='d-flex flex-column gap-1'>
          <label className='label-medium'>Duration</label>
          <ScopedBlock missingScopeAction={MissingScopeAction.DISABLE} scope={{ scope: 'integration:update', superScope: ['integration:admin'] }}>
            <Controller
              name='taskDueDateOffset.value'
              control={form.control}
              rules={{
                max: {
                  value: MAX_OFFSET_VALUE,
                  message: `Value must be less than or equal to ${MAX_OFFSET_VALUE}`,
                },
                min: { value: 0, message: 'Value must be greater than or equal to 0' },
              }}
              render={({ field, fieldState }) => {
                const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
                  field.onChange(e.target.valueAsNumber ? e.target.valueAsNumber : 0)
                }
                return (
                  <>
                    <Form.Control type='number' onChange={handleChange} value={field.value ?? ''} max={MAX_OFFSET_VALUE} min={0} step={1} />
                    {fieldState.error && <FormFeedback>{fieldState.error.message}</FormFeedback>}
                  </>
                )
              }}
            />
          </ScopedBlock>
        </Col>
        <Col className='d-flex flex-column gap-1'>
          <label className='label-medium'>Unit of time</label>
          <ScopedBlock missingScopeAction={MissingScopeAction.DISABLE} scope={{ scope: 'integration:update', superScope: ['integration:admin'] }}>
            <Controller
              name='taskDueDateOffset.unit'
              control={form.control}
              render={({ field }) => (
                <SelectDropdown
                  options={unitOptions}
                  initialSelectedOption={unitOptions.find((e) => e.id === field.value)}
                  onOptionSelected={(e) => field.onChange(e.id)}
                />
              )}
            />
          </ScopedBlock>
        </Col>
      </Row>
      {form.getValues('taskDueAllDay') && (
        <Row className='mb-3'>
          <Row>
            <h6>Task deadline</h6>
          </Row>
          <Row className='mb-3'>
            <ScopedBlock missingScopeAction={MissingScopeAction.DISABLE} scope={{ scope: 'integration:update', superScope: ['integration:admin'] }}>
              <Controller
                name='taskDueAllDay'
                control={form.control}
                render={({ field }) => {
                  return (
                    <div>
                      <Form.Check
                        inline
                        name='taskDueAllDay.allDay-group'
                        type='radio'
                        label='Task deadline is a whole day'
                        id='taskDueAllDay.allDay-form-1'
                        checked={field.value?.allDay === true}
                        onChange={() => {
                          field.onChange({ allDay: true, date: null })
                          setIsDatePickerValid(true)
                        }}
                      />
                      <div className='d-flex gap-3'>
                        <Form.Check
                          className='flex-shrink-0'
                          inline
                          name='taskDueAllDay.allDay-group'
                          type='radio'
                          label='Task deadline is a specified hour of a day'
                          id='taskDueAllDay.allDay-form-2'
                          checked={field.value?.allDay === false}
                          onChange={() => {
                            const defaultTime = new Date()
                            defaultTime.setUTCHours(16, 0, 0, 0)
                            field.onChange({ allDay: false, date: defaultTime })
                            setIsDatePickerValid(true)
                          }}
                        />
                        <DatePicker
                          className='p-0 w-100 position-relative'
                          selected={field.value?.date}
                          onChange={(date) => {
                            field.onChange({ ...field.value, date: date })
                            if (date != null) {
                              setIsDatePickerValid(true)
                            } else {
                              setIsDatePickerValid(false)
                            }
                          }}
                          showTimeSelect
                          showTimeSelectOnly
                          timeIntervals={15}
                          timeCaption='Time'
                          dateFormat='h:mm aa'
                          disabled={field.value?.allDay}
                        />
                      </div>
                    </div>
                  )
                }}
              />
            </ScopedBlock>
          </Row>
        </Row>
      )}

      <Row>
        <Col>
          <div className='d-flex gap-3 justify-content-end mb-4'>
            <Button variant='secondary' onClick={handleReset} disabled={isUpdateUserIntegrationLoading}>
              Reset
            </Button>
            <ScopedBlock missingScopeAction={MissingScopeAction.DISABLE} scope={{ scope: 'integration:update', superScope: ['integration:admin'] }}>
              <Button variant='primary' onClick={form.handleSubmit(onSubmit)} loading={isUpdateUserIntegrationLoading} disabled={!isDatePickerValid}>
                Save
              </Button>
            </ScopedBlock>
          </div>
        </Col>
      </Row>
    </Container>
  )
}
